import { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HoneypotField, readHoneypot, submitErrorMessage } from "@/components/HoneypotField";
import axios from "axios";
import { API } from "@/App";
import { useSettings } from "@/context/SettingsContext";
import { MapPin, Phone, Mail, Clock, Send, Star } from "lucide-react";

export const ContactPage = () => {
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const website = readHoneypot(e);
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contacts`, { ...formData, website });
      toast.success("Votre message a été envoyé avec succès !");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      toast.error(submitErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Header />
      
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <p className="font-cinzel text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Contact
          </p>
          <h1 className="font-serif text-4xl md:text-6xl text-white mb-6">
            Parlons de votre projet
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Notre équipe est à votre disposition pour répondre à toutes vos questions
            et vous accompagner dans votre projet immobilier.
          </p>
        </div>
      </section>
      
      {/* Contact Info + Form */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="font-serif text-2xl mb-6">Nos coordonnées</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#D4AF37]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Adresse</h3>
                      <p className="text-gray-600 whitespace-pre-line">
                        {settings.address}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#D4AF37]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Téléphone</h3>
                      <a href={`tel:${settings.phone?.replace(/\s/g, '')}`} className="text-gray-600 hover:text-[#D4AF37] transition-colors">
                        {settings.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#D4AF37]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email</h3>
                      <a href={`mailto:${settings.email}`} className="text-gray-600 hover:text-[#D4AF37] transition-colors">
                        {settings.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-[#D4AF37]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Horaires</h3>
                      <p className="text-gray-600 whitespace-pre-line" data-testid="contact-hours">
                        {settings.hours}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="text-[#D4AF37]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Votre avis compte</h3>
                      <a
                        href="https://g.page/r/CR3dspobMw_YEAE/review"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-5 py-2.5 text-xs uppercase tracking-wider mt-1"
                        data-testid="contact-google-review"
                      >
                        <Star size={14} /> Déposer un avis Google
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Localisation (sans traceur : Google n'est appelé qu'au clic) */}
              <div className="bg-[#0A0A0A] text-white p-8 rounded-sm" data-testid="contact-map-card">
                <p className="font-cinzel text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Nous trouver</p>
                <p className="font-serif text-2xl mb-2">Le Loroux-Bottereau</p>
                <p className="text-gray-400 text-sm whitespace-pre-line mb-6">{settings.address}</p>
                <p className="text-gray-400 text-sm mb-6">Au cœur du Vignoble nantais, à 20 minutes de Nantes.</p>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((settings.address || "").replace(/\s*\n\s*/g, ", "))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-5 py-2.5 text-xs uppercase tracking-wider"
                  data-testid="contact-open-maps"
                >
                  <MapPin size={14} /> Ouvrir dans Google Maps
                </a>
                <p className="text-[11px] text-gray-500 mt-4">Aucune carte n'est chargée automatiquement : Google Maps ne s'ouvre qu'à votre demande.</p>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#F9F9F9] p-8 md:p-12" data-testid="contact-form">
                <h2 className="font-serif text-2xl mb-2">Envoyez-nous un message</h2>
                <p className="text-gray-600 mb-8">
                  Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6" style={{ position: "relative" }}>
                  <HoneypotField />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Votre nom"
                        required
                        data-testid="contact-form-name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="votre@email.com"
                        required
                        data-testid="contact-form-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="06 00 00 00 00"
                        data-testid="contact-form-phone"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject">Sujet *</Label>
                      <Select
                        value={formData.subject || undefined}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger data-testid="contact-form-subject">
                          <SelectValue placeholder="Sélectionnez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="estimation">Demande d'estimation</SelectItem>
                          <SelectItem value="visite">Demande de visite</SelectItem>
                          <SelectItem value="achat">Projet d'achat</SelectItem>
                          <SelectItem value="vente">Projet de vente</SelectItem>
                          <SelectItem value="location">Recherche de location</SelectItem>
                          <SelectItem value="gestion">Gestion locative</SelectItem>
                          <SelectItem value="autre">Autre demande</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Décrivez votre projet ou votre demande..."
                      required
                      data-testid="contact-form-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full md:w-auto bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A] px-12"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                  >
                    {submitting ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
